<template>
  <v-container>
    <AppFilterNavigation
      ref="filters"
      :filters.sync="filters"
      filter-name="peopleFilters"
    >
      <template #default="prop">
        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppSelectRegions
            v-model="filters.representingRegionIds"
            :label="$t('people:filters.regions.label')"
            item-value="id"
            item-text="name"
            data-test="region"
            :menu-props="customMenuOptions"
            multiple
            clearable
            is-filter
            include-disabled
          />
        </v-col>
        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppSelectOrganisation
            v-model="filters.clubIds"
            :menu-props="customMenuOptions"
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            multiple
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppSelectDiscipline
            v-model="filters.profQualificationDisciplineIds"
            :menu-props="customMenuOptions"
            data-test="qualificationDisciplines"
            clearable
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            hide-details
            :label="
              $t(
                'people:filters.professionsDiscipline.label',
                'Professions in disciplines',
                'Label of the people professions in disciplines filter'
              )
            "
          />
        </v-col>

        <v-col cols="12" md="2" class="px-1 mb-2 text-center">
          <AppSelectPersonQualifications
            v-model="filters.profQualificationIds"
            :menu-props="customMenuOptions"
            filter-input
            clearable
            ignore-checkboxes-view
            hide-details
            multiple
            :label="
              $t(
                'people:filters.professions.label',
                'Professions',
                'Label of the people professions filter'
              )
            "
          />
        </v-col>

        <v-col
          v-if="$vuetify.breakpoint.mdAndUp"
          cols="12"
          md="4"
          class="px-1 mb-2 text-center"
        >
          <AppSearchBox :value.sync="filters.search" clearable />
        </v-col>
      </template>

      <template #advanced="prop">
        <v-row>
          <v-col cols="12" md="3">
            <div class="px-1 mb-2 text-center">
              <AppSelectLicenceCategory
                v-model="filters.licenceCategories"
                :label="
                  $t(
                    'people:filters.licenceCategory.label',
                    'Licence category',
                    'Label of the people licence category filter'
                  )
                "
                :input-props="{
                  menuProps: customMenuOptions,
                  clearable: true,
                  filterInput: true,
                  hideDetails: true,
                  multiple: true,
                  dataTest: 'licenceCategory'
                }"
              />
            </div>

            <div class="px-1 mb-2 text-center">
              <AppSelectYear
                v-model="filters.seasonLicenceForOneOfYears"
                :start-year="2019"
                :input-props="{
                  filterInput: true,
                  label: $t(
                    'people:filters.seasonLicenceForOneOfYears.label',
                    'Seasonal licence'
                  ),
                  hint: $t(
                    'people:filters.seasonLicenceForOneOfYears.hint',
                    'Has seasonal licence for one of the selected years'
                  ),
                  persistentHint: true,
                  clearable: true,
                  multiple: true,
                  dataTest: 'seasonLicenceForOneOfYears'
                }"
              />
            </div>

            <div class="px-1 mb-2 text-center">
              <AppGenericSelect
                v-model="filters.statusAndLicence"
                :items="[
                  {
                    name: $t(
                      'people:filter.status.active.label',
                      'Active',
                      'Label for status filter active'
                    ),
                    group: $t(
                      'people:filter.status.group.label',
                      'Status',
                      'Label for status group in status filter'
                    ),
                    id: 'status_active'
                  },
                  {
                    name: $t(
                      'people:filter.status.inactive.label',
                      'Inactive',
                      'Label for status filter active'
                    ),
                    group: $t(
                      'people:filter.status.group.label',
                      'Status',
                      'Label for status group in status filter'
                    ),
                    id: 'status_inactive'
                  }
                ]"
                item-value="id"
                item-text="name"
                data-test="statusAndLicence"
                :is-filter="!prop.mobile"
                :dense="prop.mobile"
                :menu-props="customMenuOptions"
                :label="
                  $t(
                    'people:filters.statusLicence.label',
                    'Status and licence',
                    'Label of the status and licence filter'
                  )
                "
                clearable
                hide-details
                multiple
              />
            </div>
          </v-col>

          <v-col
            v-if="$auth.canAccessField('Person', 'personRoles', 'Any')"
            cols="12"
            md="3"
          >
            <div class="px-1 mb-2 text-center">
              <AppSelectRoles
                v-model="filters.roleIds"
                item-value="id"
                item-text="name"
                data-test="roles"
                :label="$t('people:filter.activeRoles.label')"
                :is-filter="!prop.mobile"
                :dense="prop.mobile"
                :menu-props="customMenuOptions"
                clearable
                hide-details
                multiple
              />
            </div>
            <div class="px-1 mb-2 text-center">
              <AppSelectRegions
                v-model="filters.roleInRegionIds"
                :label="
                  $t(
                    'people:filters.roleinRegion.label',
                    'Role in region',
                    'Label of the role in region filter'
                  )
                "
                item-value="id"
                item-text="name"
                data-test="roleRegion"
                :menu-props="customMenuOptions"
                multiple
                clearable
                is-filter
                include-disabled
              />
            </div>
            <div class="px-1 mb-2 text-center">
              <AppSelectDiscipline
                v-model="filters.roleInDisciplineIds"
                item-value="id"
                item-text="name"
                data-test="roleDiscipline"
                :menu-props="customMenuOptions"
                is-filter
                clearable
                hide-details
                multiple
                :label="
                  $t(
                    'people:filters.roleinDiscipline.label',
                    'Role in discipline',
                    'Label of the role in discipline filter'
                  )
                "
              />
            </div>
          </v-col>

          <v-col
            v-if="
              $auth.canAccessField(
                'Person',
                'personOrganisationFunctions',
                'Any'
              )
            "
            cols="12"
            md="3"
          >
            <div class="px-1 mb-2 text-center">
              <AppSelectOrganisationalFunction
                v-model="filters.organisationalFunctionIds"
                :label="
                  $t('people:filter.activeFunctions.label', 'Active functions')
                "
                :input-props="{
                  filterInput: true,
                  clearable: true,
                  multiple: true
                }"
              />
            </div>
            <div class="px-1 mb-2 text-center">
              <AppSelectOrganisation
                v-model="filters.organisationIdsForFunction"
                :menu-props="customMenuOptions"
                :label="
                  $t(
                    'people:filter.functionsInOrganisations.label',
                    'Functions in organisations'
                  )
                "
                :query-variables="{
                  hasActivePersonOrganisationFunctions: true
                }"
                :is-filter="!prop.mobile"
                :dense="prop.mobile"
                multiple
                clearable
                hide-details
              />
            </div>
          </v-col>

          <v-col cols="12" md="3">
            <div class="px-1 mb-2 text-center">
              <AppSelectCountries
                v-model="filters.countryIds"
                :prepend="[
                  {
                    id: 'not_set',
                    name: $t(
                      'people:filter.countries.norSet.label',
                      'Not set',
                      'Label for countries filter not set'
                    )
                  }
                ]"
                :input-props="{
                  clearable: true,
                  menuProps: customMenuOptions,
                  multiple: true,
                  dense: prop.mobile,
                  hideDetails: true
                }"
                :label="$t('people:filter.countries.label')"
                data-test="countries"
                :is-filter="!prop.mobile"
              />
            </div>

            <div class="px-1 mb-2 text-center">
              <AppGenericInput
                v-model="filters.feiItems"
                component-name="v-select"
                :items="FeiItems"
                filter-input
                item-value="id"
                item-text="name"
                :label="$t('people:search.filter.FEIFilter.label', 'FEI')"
                data-test="FeiStatus"
                clearable
                multiple
                hide-no-data
                hide-details
              />
            </div>
          </v-col>
        </v-row>
      </template>

      <template #mobile>
        <AppSearchBox :value.sync="filters.search" class="px-4" />
      </template>
    </AppFilterNavigation>

    <div class="d-flex my-auto justify-end pa-3 flex-wrap">
      <v-btn
        v-if="$auth.canAccessRoute('PersonCreationPage')"
        :to="{ name: 'PersonCreationPage' }"
        large
        text
        color="edit"
      >
        <b>
          <v-icon class="mr-1">$edit</v-icon>
          {{ $t("people:button.addPerson", "Person") }}
        </b>
      </v-btn>

      <v-btn
        v-if="$auth.checkUserCanPerformMutationsInScopeAny('personMerge')"
        :to="{ name: 'PersonDuplicateMerge' }"
        target="_blank"
        large
        text
        color="edit"
      >
        <b>
          <v-icon class="mr-1">$iconMerge</v-icon>
          {{ $t("people:common.mergeDuplicates.button", "Merge duplicates") }}
        </b>
      </v-btn>
      <AppExportFile
        v-if="$auth.canAccessRoute('PersonCreationPage')"
        :prepare-function="exportPeople"
        use-tooltip
      >
        <template #tooltip>
          {{
            $t(
              "common:downloadCsv.tooltip",
              "Limited to 1 500 records. Please use filters to narrow down the search and give time for download to finish"
            )
          }}
        </template>
      </AppExportFile>
    </div>

    <v-card color="primary" class="fipa--header" tile />

    <SearchTemplatePeople
      data-test="people"
      :items="people"
      :loading="$apollo.queries.people.loading"
      :hide-default-header="$vuetify.breakpoint.smAndDown"
    />
    <div class="text-right">
      <AppLoadMoreBtn
        :loading="$apollo.queries.people.loading"
        :has-more-results="hasMoreResults"
        @loadMore="loadMore"
      />
    </div>
  </v-container>
</template>

<script>
import gql from "fraql";
import { useDisciplineHelper } from "@composables/masterdata/competitions/disciplines";

import AppSelectOrganisation from "@/components/elements/AppSelects/AppSelectOrganisation.vue";
import AppSelectCountries from "@/components/elements/AppSelects/AppSelectCountries.vue";
import AppSelectPersonQualifications from "@/components/elements/AppSelects/AppSelectPersonQualifications.vue";
import AppSelectRoles from "@/components/elements/AppSelects/AppSelectRoles.vue";
import AppSelectDiscipline from "@AppSelects/AppSelectDiscipline.vue";
import AppSelectRegions from "@AppSelects/AppSelectRegions.vue";
import AppFilterNavigation from "@/components/elements/AppFilterNavigation.vue";
import AppSearchBox from "@/components/elements/AppSearchBox.vue";
import AppLoadMoreBtn from "@/components/elements/AppLoadMoreBtn.vue";
import AppSelectYear from "@AppSelects/AppSelectYear.vue";
import AppSelectLicenceCategory from "@AppSelects/AppSelectLicenceCategory.vue";
import SearchTemplatePeople from "@/components/table_templates/People.vue";
import AppGenericInput from "@AppSelects/AppGenericInput.vue";

import AppGenericSelect from "@/components/elements/AppSelects/AppGenericSelect.vue";
import AppExportFile from "@/components/elements/AppExportFile.vue";
import AppSelectOrganisationalFunction from "@AppSelects/AppSelectOrganisationalFunction.vue";

const FILTERS = `
  $offset: Int
  $limit: Int
  $keyword: String
  $licenceCategoryIds: [ID]
  $profQualificationIds: [ID]
  $profQualificationGroupIds: [ID]
  $profQualificationDisciplineIds: [ID]
  $personOrganisationFunctionFilter: PersonOrganisationFunctionFilterInput
  $personIds: [ID]
  $sortBy: String
  $sortDesc: Boolean
  $clubIds: [ID]
  $countryIds: [ID]
  $currentSeasonLicenceValid: Boolean
  $hasActiveIdentifiers: Boolean
  $hasIdentifiersFrom: [String]
  $roleIds: [ID]
  $roleInRegionIds: [ID]
  $roleInDisciplineIds: [ID]
  $representingRegionIds: [ID]
  $seasonLicenceForOneOfYears: [Int]
  $hasFeiid: Boolean
  $feiLicenceValidForDisciplineIds: [ID]
`;

const FILTER_VALUES = `
  offset: $offset
  limit: $limit
  keyword: $keyword
  licenceCategoryIds: $licenceCategoryIds
  profQualificationIds: $profQualificationIds
  profQualificationGroupIds: $profQualificationGroupIds
  profQualificationDisciplineIds: $profQualificationDisciplineIds
  personOrganisationFunctionFilter: $personOrganisationFunctionFilter
  personIds: $personIds
  sortBy: $sortBy
  sortDesc: $sortDesc
  clubIds: $clubIds
  countryIds: $countryIds
  currentSeasonLicenceValid: $currentSeasonLicenceValid
  hasActiveIdentifiers: $hasActiveIdentifiers
  hasIdentifiersFrom: $hasIdentifiersFrom
  roleIds: $roleIds
  roleInRegionIds: $roleInRegionIds
  roleInDisciplineIds: $roleInDisciplineIds
  representingRegionIds: $representingRegionIds
  seasonLicenceForOneOfYears: $seasonLicenceForOneOfYears
  hasFeiid: $hasFeiid
  feiLicenceValidForDisciplineIds: $feiLicenceValidForDisciplineIds
`;
export default {
  name: "PeopleSearchPage",
  components: {
    AppSelectOrganisation,
    AppSelectCountries,
    AppSelectPersonQualifications,
    AppSelectRoles,
    SearchTemplatePeople,
    AppSearchBox,
    AppFilterNavigation,
    AppGenericSelect,
    AppSelectDiscipline,
    AppLoadMoreBtn,
    AppExportFile,
    AppSelectRegions,
    AppSelectYear,
    AppSelectLicenceCategory,
    AppGenericInput,
    AppSelectOrganisationalFunction
  },
  setup() {
    return {
      ...useDisciplineHelper()
    };
  },
  data() {
    return {
      people: [],
      inactiveComponent: false,
      customMenuOptions: {
        closeOnContentClick: true
      },
      hasMoreResults: true,
      sortBy: "name",
      sortDesc: false,
      filtersCheck: true,
      filters: {
        organisationalFunctionIds: [],
        organisationIdsForFunction: [],

        clubIds: [],
        licenceCategories: [], // Must be an array reference!
        profQualificationIds: [],
        profQualificationGroupIds: [],
        profQualificationDisciplineIds: null,
        statusAndLicence: [],
        countryIds: [],
        roleIds: [],
        roleInRegionIds: [],
        roleInDisciplineIds: [],
        search: "",
        representingRegionIds: [],
        feiItems: []
      }
    };
  },
  computed: {
    fields() {
      return [
        {
          value: "name",
          text: this.$t("people:fields.name.label", "Name", "Name field label")
        },
        {
          value: "clubName",
          text: this.$t(
            "people:fields.clubCountry.label",
            "Club / Country",
            "Name field label"
          ),
          hideMobile: true
        },
        {
          value: "licenceCategoryName",
          text: "",
          hideMobile: true,
          guardedByField: "Person.licenceCategoryId"
        },
        {
          value: "licenceNumber",
          text: "",
          hideMobile: true,
          guardedByField: "Person.identifier"
        },
        {
          value: "qualifications",
          text: "",
          guardedByField: "Person.personProfQualifications"
        }
      ];
    },
    hasActiveIdentifiersFromFilter() {
      if (
        this.filters.statusAndLicence.includes("status_active") &&
        this.filters.statusAndLicence.includes("status_inactive")
      ) {
        return null; // in case both are selected then no filter
      }
      if (this.filters.statusAndLicence.includes("status_active")) {
        return true;
      }
      if (this.filters.statusAndLicence.includes("status_inactive")) {
        return false;
      }
      return null;
    },
    currentSeasonLicenceValidFromFilter() {
      if (
        this.filters.statusAndLicence.includes("licence_valid") &&
        this.filters.statusAndLicence.includes("licence_invalid")
      ) {
        return null; // in case both are selected then no filter
      }
      if (this.filters.statusAndLicence.includes("licence_valid")) {
        return true;
      }
      if (this.filters.statusAndLicence.includes("licence_invalid")) {
        return false;
      }
      return null;
    },
    hasIdentifiersFromFilter() {
      let selectedIdentifierOrigins = [];
      if (this.filters.statusAndLicence.includes("sense")) {
        selectedIdentifierOrigins.push("sense");
      }
      if (this.filters.statusAndLicence.includes("kipa")) {
        selectedIdentifierOrigins.push("kipa");
      }
      return selectedIdentifierOrigins;
    },
    seasonYears() {
      const range = this.$dayjs().format("YYYY") - 2019;
      const years = [];
      for (let i = 0; range > i; i++) {
        years.unshift(2019 + i);
      }
      return years;
    },
    FeiItems() {
      const items = [
        {
          header: this.$t(
            "people:search.filter.FEIFilter.statusSubFilter.title",
            "Status"
          )
        },
        {
          name: this.$t(
            "people:search.filter.FEIFilter.hasFEIID.label",
            "Has FEI ID"
          ),
          id: "hasFeiid"
        },
        { divider: true },
        {
          header: this.$t(
            "people:search.filter.FEIFilter.hasValidRegistration.title",
            "Has valid FEI registration"
          )
        }
      ];
      this.feiDisciplines.forEach(x => {
        x.name = x.i18n;
        items.push(x);
      });
      return items;
    },
    personOrganisationFunctionFilter() {
      if (
        this.filters.organisationalFunctionIds.length ||
        this.filters.organisationIdsForFunction.length
      ) {
        return {
          validCurrently: true,
          organisationalFunctionIds: this.filters.organisationalFunctionIds
            .length
            ? this.filters.organisationalFunctionIds
            : undefined,
          organisationIds: this.filters.organisationIdsForFunction.length
            ? this.filters.organisationIdsForFunction
            : undefined
        };
      }

      return undefined;
    },
    filterVariables() {
      return {
        offset: 0,
        limit: 50,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        keyword: this.filters.search ? this.filters.search : null,
        clubIds: this.filters.clubIds,
        licenceCategoryIds: this.filters.licenceCategories,
        profQualificationIds: this.filters.profQualificationIds,
        profQualificationGroupIds: this.filters.profQualificationGroupIds,
        profQualificationDisciplineIds:
          this.filters.profQualificationDisciplineIds,
        countryIds: this.filters.countryIds,
        currentSeasonLicenceValid: this.currentSeasonLicenceValidFromFilter,
        hasActiveIdentifiers: this.hasActiveIdentifiersFromFilter,
        hasIdentifiersFrom: this.hasIdentifiersFromFilter,
        roleIds: this.filters.roleIds,
        roleInRegionIds: this.filters.roleInRegionIds,
        roleInDisciplineIds: this.filters.roleInDisciplineIds,
        representingRegionIds: this.filters.representingRegionIds,
        seasonLicenceForOneOfYears: this.filters.seasonLicenceForOneOfYears,

        personOrganisationFunctionFilter: this.personOrganisationFunctionFilter,

        hasFeiid: this.filters.feiItems.includes("hasFeiid"),
        feiLicenceValidForDisciplineIds: this.filters.feiItems.filter(x =>
          this.feiDisciplineIds.includes(x)
        )
      };
    }
  },
  mounted() {
    this.$refs.filters.findURLParams();
    // Find if any params in URL and then send query
    // Prevents sending double queries, one and without variables
    this.filtersCheck = false;
  },
  activated() {
    this.inactiveComponent = false;
  },
  deactivated() {
    this.inactiveComponent = true;
  },
  apollo: {
    people: {
      query: gql`
        query peopleSearch (${FILTERS}) {
          peopleSearch (${FILTER_VALUES}) {
            ${SearchTemplatePeople.fragments.people}
          }
        }
      `,
      skip() {
        return this.filtersCheck || this.inactiveComponent;
      },
      variables() {
        return this.filterVariables;
      },
      update({ peopleSearch }) {
        this.hasMoreResults = peopleSearch.length >= 50;
        return peopleSearch;
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    loadMore() {
      this.$apollo.queries.people
        .fetchMore({
          variables: {
            offset: this.people.length
          }
        })
        .then(({ data: { peopleSearch } }) => {
          this.hasMoreResults = peopleSearch.length >= 50;
        });
    },
    async exportPeople() {
      const keyMap = [
        {
          column: this.$t(
            "people:CsvKeys.ID.label",
            "ID",
            "Csv ID column label"
          ),
          key: "id"
        },
        {
          column: this.$t(
            "people:CsvKeys.LastName.label",
            "Last name",
            "Csv ID column label"
          ),
          key: "lastName"
        },
        {
          column: this.$t(
            "people:CsvKeys.FirstName.label",
            "First name",
            "Csv ID column label"
          ),
          key: "firstName"
        },
        {
          column: this.$t(
            "people:CsvKeys.club.label",
            "Club",
            "Csv ID column label"
          ),
          key: "clubName"
        },
        {
          column: this.$t(
            "people:CsvKeys.representingRegion.label",
            "Representing region"
          ),
          key: "representingRegion.name"
        },
        {
          column: this.$t(
            "people:CsvKeys.Region.label",
            "Region",
            "Csv ID column label"
          ),
          key: "club.region.name"
        },
        {
          column: this.$t(
            "people:CsvKeys.topRegionOrganisation.label",
            "Top organsation (league)"
          ),
          key: "representingClub.topRegionOrganisation.name"
        },
        {
          column: this.$t(
            "people:CsvKeys.Country.label",
            "Country",
            "Csv ID column label"
          ),
          useGivenMethod: person => {
            return person.representingCountry?.ioc || "";
          }
        },
        {
          column: this.$t(
            "people:CsvKeys.LicenceCategory.label",
            "Licence category",
            "Csv ID column label"
          ),
          key: "licenceCategoryName"
        },
        {
          column: this.$t(
            "people:CsvKeys.LicenceValid.label",
            "Licence valid",
            "Csv ID column label"
          ),
          key: "currentSeasonLicenceValid",
          useGivenMethod: person => {
            return person.currentSeasonLicenceValid ? "Valid" : "Invalid";
          }
        },
        {
          column: this.$t(
            "people:CsvKeys.LastLicencePaymentDate.label",
            "Last licence payment date",
            "Csv ID column label"
          ),
          key: "lastYearlyLicencePaidDate"
        },
        {
          column: this.$t(
            "people:CsvKeys.insurancesForCurrentYear.label",
            "Current year insurances"
          ),
          key: "insurancesForCurrentYear",
          additionalProps: {
            isArray: true,
            fieldKey: "insuranceCategory.name",
            toString: true
          }
        },
        {
          column: this.$t(
            "people:CsvKeys.LicenceNr.label",
            "Licence number",
            "Csv ID column label"
          ),
          key: "identifier",
          useGivenMethod: item => {
            return item.identifier || item.identifierMasked;
          }
        },
        {
          column: this.$t(
            "people:CsvKeys.FEIID.label",
            "FEI ID",
            "Csv ID column label"
          ),
          key: "feiid"
        },
        {
          column: this.$t(
            "people:CsvKeys.Professions.label",
            "Professions",
            "Csv ID column label"
          ),
          key: "personProfQualificationsValidCurrently",
          additionalProps: {
            isArray: true,
            fieldKey: "profQualification.name",
            toString: true
          }
        },
        {
          column: this.$t(
            "people:CsvKeys.personOrganisationFunctionsValidCurrently.label",
            "Current administrative functions"
          ),
          key: "personOrganisationFunctionsValidCurrently",
          additionalProps: {
            isArray: true,
            useGivenMethod(validFunctions) {
              return validFunctions
                .filter(Boolean)
                .map(func => {
                  let name = func.organisationalTitle.name;
                  const funcName = func.organisationalFunction.name;

                  if (funcName) {
                    name += ` (${funcName})`;
                  }
                  return name;
                })
                .join(", ");
            }
          }
        },
        {
          column: this.$t(
            "people:CsvKeys.DateOfBirth.label",
            "Date of birth",
            "Csv ID column label"
          ),
          key: "dateOfBirth"
        },
        {
          column: this.$t(
            "people:CsvKeys.age.label",
            "Age",
            "Csv ID column label"
          ),
          key: "age"
        },
        {
          column: this.$t(
            "people:CsvKeys.email.label",
            "Email",
            "Csv ID column label"
          ),
          key: "email"
        },
        {
          column: this.$t(
            "people:CsvKeys.phone.label",
            "Phone",
            "Csv ID column label"
          ),
          key: "phone"
        },
        {
          column: this.$t(
            "people:CsvKeys.address.label",
            "Address",
            "Csv ID column label"
          ),
          key: "address.addressText"
        },
        {
          column: this.$t("people:CsvKeys.addressHouse.label", "Address house"),
          key: "address.addressHouse"
        },
        {
          column: this.$t("people:CsvKeys.addressBox.label", "Box"),
          key: "address.addressBox"
        },
        {
          column: this.$t(
            "people:CsvKeys.city.label",
            "City",
            "Csv ID column label"
          ),
          key: "address.addressCity"
        },
        {
          column: this.$t(
            "people:CsvKeys.index.label",
            "Index",
            "Csv ID column label"
          ),
          key: "address.addressIndex"
        },
        {
          column: this.$t("people:CsvKeys.addressCountry.label", "Country"),
          key: "address.addressCountry.ioc"
        },
        {
          column: this.$t(
            "people:CsvKeys.LastUpdate.label",
            "Last update",
            "Csv ID column label"
          ),
          key: "updatedAt"
        }
      ];

      const { data } = await this.$apollo.query({
        query: gql`
          query peopleSearchExport(
           ${FILTERS}
          ) {
            peopleSearch(
              ${FILTER_VALUES}
            ) {
              id
              name
              firstName
              lastName
              licenceCategoryName
              currentSeasonLicenceValid
              lastYearlyLicencePaidDate
              identifier
              feiid
              dateOfBirth
              email
              phone
              address {
                id
                addressText
                addressCity
                addressIndex
                addressHouse
                addressBox
                addressCountryId
                addressCountry @client {
                  id
                  ioc
                }
              }

              representingClub {
                id
                topRegionOrganisation {
                  id
                  name
                }
              }

              representingRegionId
              representingRegion @client {
                id
                name
              }
              
              representingCountryId
              representingCountry @client {
                id
                ioc
              }

              age
              clubName
              updatedAt
              club {
                id
                region {
                  id
                  name
                }
              }

              insurancesForCurrentYear {
                id
                insuranceCategoryId
                insuranceCategory @client {
                  id
                  name
                }
              }

              personProfQualificationsValidCurrently {
                id
                profQualification {
                  id
                  name
                }
              }

              personOrganisationFunctionsValidCurrently {
                id
                organisationalTitleId
                organisationalTitle @client {
                  id
                  name
                }
                organisationalFunctionId
                organisationalFunction @client {
                  id
                  name
                }
              }
            }
          }
        `,
        variables: {
          ...this.filterVariables,
          limit: 1000,
          offset: 0
        }
      });

      this.$apollo.queries.people.refetch({
        offset: 0
      });

      return {
        keyMap,
        items: data.peopleSearch,
        filename: "people-export"
      };
    }
  }
};
</script>
